import { route } from 'quasar/wrappers'
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory, ErrorTypes, isNavigationFailure, NavigationFailure, NavigationFailureType,
  RouteLocationNormalized
} from 'vue-router'

import routes, { ROUTE_NAME_LOGIN } from './routes'
import { Platform } from 'quasar'
import { useAuthUserStore } from 'stores/auth-user-store'
import { useHistoryStore } from 'stores/history-store'
import { axiosRequestManager } from 'boot/axios'

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : (process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory)

  const router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE)
  })

  // Add referrer to every request
  router.beforeEach(async (to, from) => {
    Object.defineProperty(
      document,
      'referrer',
      {
        configurable: true,
        value: from.fullPath ? window.location.origin + from.fullPath : ''
      }
    )
  })

  // Login guard
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthUserStore()
    if (to.name !== ROUTE_NAME_LOGIN && !authStore.isLoggedIn) {
      next({ name: ROUTE_NAME_LOGIN, query: { redirect: to.fullPath } })
    } else {
      next()
    }
  })

  // collect navigation history
  router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized, failure) => {
    if (isNavigationFailure(failure)) {
      return
    }

    if (to.name) {
      const historyStore = useHistoryStore()
      historyStore.addHistoryEntry(to)
    }
  })

  router.afterEach((to, from, failure) => {
    if (failure && !isNavigationFailure(failure)) {
      console.error('Router navigation failure', failure)
    }
  })

  router.beforeEach((to, from) => {
    if (from.name !== ROUTE_NAME_LOGIN) {
      axiosRequestManager.cancelRequests()
    }
  })

  router.afterEach(() => {
    // TODO: use vue scroll: https://v3.router.vuejs.org/guide/advanced/scroll-behavior.html
    window.scrollTo(0, 0)
  })

  router.afterEach(async (to) => {
    if (Platform.within.iframe) {
      // TODO: remove? We do not need iframe
      // TODO: Restrict to https://stockpulse.ai
      // const targetOrigin = 'https://stockpulse.ai'
      const targetOrigin = '*'

      window.parent.postMessage({ type: 'navigation', data: { to: to.fullPath } }, targetOrigin)
    }
  })

  return router
})
